import { useState } from 'react';
import { ActionIcon, Text } from '@mantine/core';
import { IconCheck, IconCode, IconCopy } from '@tabler/icons-react';
import { Cmp, logger } from '~portal/lib';
import { touchTarget } from '~portal/styles/touch-target.css';
import { typo } from '../typography.css';

export type CodeBlockProps = {
  code: string;
  language?: string;
  'data-variant'?: string;
};

/**
 * Fenced code block per the Charlie markdown style guide: surface card with a
 * header row (code glyph + language label, copy control) above the code.
 */
export const CodeBlock: Cmp<CodeBlockProps> = function ({
  code,
  language,
  'data-variant': variant,
}) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard
      .writeText(code)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      })
      .catch((err) => {
        logger.warn('[CodeBlock] Clipboard write failed', {
          error: err instanceof Error ? err.message : String(err),
        });
      });
  };

  return (
    <div className={typo.md.code.blockCard} data-variant={variant}>
      <div className={typo.md.code.blockHeader}>
        <Text component="span" className={typo.md.code.blockLanguage}>
          <IconCode size={16} aria-hidden />
          {language ? language.charAt(0).toUpperCase() + language.slice(1) : 'Code'}
        </Text>
        <ActionIcon
          variant="subtle"
          color="gray"
          size="sm"
          className={touchTarget}
          onClick={handleCopy}
          aria-label="Copy code"
        >
          {copied ? <IconCheck size={16} /> : <IconCopy size={16} />}
        </ActionIcon>
      </div>
      <pre className={typo.md.code.pre}>
        <code>{code}</code>
      </pre>
    </div>
  );
};
